<script setup>
import { isReportAppLink } from './siteLinks.js';
import { computed, onMounted } from 'vue';
import { buttonStyle } from './buttons.js';
import { iconLibrary, loadIconLibrary } from './siteIcons.js';

// One button, drawn the same in both templates. The template supplies
// the corners through --r (Trailhead) or falls back to 8px (Classic).
const props = defineProps({
    item: { type: Object, required: true },
});

onMounted(() => { if (props.item.icon) loadIconLibrary(); });

// From our own registry, never from the block's config, so this
// v-html only ever holds markup we ship. Monochrome, so a two-tone
// glyph (the maintenance types, the planner pins) takes the button's
// icon color like every other icon instead of keeping its own.
const iconMarkup = computed(() => {
    const lib = iconLibrary.value;
    if (!lib || !props.item.icon) return '';
    return lib.iconSvg(props.item.icon, { size: 24, monochrome: true });
});
const tag = computed(() => (props.item.href ? 'a' : 'span'));
const attrs = computed(() => {
    if (!props.item.href) return {};
    const a = { href: props.item.href };
    // The Report app link never opens a tab: see siteLinks.js.
    if (props.item.new_tab && !isReportAppLink(props.item.href)) { a.target = '_blank'; a.rel = 'noopener'; }
    return a;
});
</script>

<template>
    <component :is="tag" v-bind="attrs"
               class="tiq-sb"
               :class="[`tiq-sb--${item.kind}`, `tiq-sb--${item.shape}`, `tiq-sb--${item.size}`, `tiq-sb--h-${item.h_align || 'left'}`, `tiq-sb--v-${item.v_align || (item.kind === 'card' ? 'top' : 'center')}`, { 'tiq-sb--bordered': item.border, 'tiq-sb--sized-icon': item.icon_size, 'tiq-sb--photo': item.image }]"
               :style="buttonStyle(item)"
               :aria-label="item.kind === 'icon' ? (item.title || null) : null"
               :title="item.kind === 'icon' && item.title ? item.title : null">
        <span v-if="item.icon" class="tiq-sb__icon" aria-hidden="true" v-html="iconMarkup"></span>
        <span v-if="item.kind !== 'icon' && (item.title || item.text)" class="tiq-sb__words">
            <span v-if="item.title" class="tiq-sb__title">{{ item.title }}</span>
            <span v-if="item.text" class="tiq-sb__text">{{ item.text }}</span>
        </span>
    </component>
</template>

<style scoped>
.tiq-sb {
    --sb-radius: var(--r, 8px);
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: var(--sb-bg, var(--accent, #2f6a4a));
    color: var(--sb-fg, #fff);
    border: 1px solid var(--sb-border, transparent);
    border-radius: var(--sb-radius);
    text-decoration: none !important;
    line-height: 1.2;
    transition: filter .15s ease, transform .15s ease;
}
a.tiq-sb:hover { filter: brightness(1.08); }
/* A background photo under a shade, so white words read on any of
   them. The colour stays underneath while the photo loads. */
.tiq-sb--photo {
    background-image: linear-gradient(rgba(0, 0, 0, var(--sb-shade, .35)), rgba(0, 0, 0, var(--sb-shade, .35))), var(--sb-image);
    background-size: cover;
    background-position: center, var(--sb-pos, 50% 50%);
    background-repeat: no-repeat;
}
a.tiq-sb:active { transform: translateY(1px); }
a.tiq-sb:focus-visible { outline: 3px solid var(--sb-bg, var(--accent, #2f6a4a)); outline-offset: 3px; }
.tiq-sb--pill { --sb-radius: 999px; }
.tiq-sb--square { --sb-radius: 0; }

.tiq-sb__icon { flex: none; display: inline-grid; place-items: center; color: var(--sb-icon, currentColor); }
.tiq-sb__icon :deep(svg) { width: 100%; height: 100%; display: block; }
.tiq-sb__words { display: flex; flex-direction: column; gap: 2px; min-width: 0; text-align: left; }
.tiq-sb__title { font-weight: 700; }
.tiq-sb__text { font-size: .85em; opacity: .85; }

/* Button: an icon and words in a row. */
.tiq-sb--button.tiq-sb--s { padding: 8px 14px; font-size: 14px; }
.tiq-sb--button.tiq-sb--m { padding: 12px 20px; font-size: 16px; }
.tiq-sb--button.tiq-sb--l { padding: 16px 26px; font-size: 18px; gap: 12px; }
.tiq-sb--button .tiq-sb__icon { width: 1.25em; height: 1.25em; }

/* Icon only: a square, or a circle with the pill shape. */
.tiq-sb--icon { justify-content: center; padding: 0; }
.tiq-sb--icon.tiq-sb--s { width: 40px; height: 40px; }
.tiq-sb--icon.tiq-sb--m { width: 52px; height: 52px; }
.tiq-sb--icon.tiq-sb--l { width: 68px; height: 68px; }
.tiq-sb--icon .tiq-sb__icon { width: 50%; height: 50%; }

/* Box: icon on top, then a title and a few lines. */
.tiq-sb--card {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    flex: 1 1 220px;
    max-width: 380px;
    align-self: stretch;
}
.tiq-sb--card.tiq-sb--pill { --sb-radius: 28px; }
.tiq-sb--card.tiq-sb--s { padding: 16px; font-size: 15px; }
.tiq-sb--card.tiq-sb--m { padding: 22px; font-size: 17px; }
.tiq-sb--card.tiq-sb--l { padding: 30px; font-size: 19px; }
.tiq-sb--card .tiq-sb__icon { width: 1.9em; height: 1.9em; }
.tiq-sb--card .tiq-sb__words { gap: 6px; }
.tiq-sb--card .tiq-sb__title { font-size: 1.25em; line-height: 1.15; }
.tiq-sb--card .tiq-sb__text { font-size: .9em; line-height: 1.45; }

/* A chosen icon size outranks the one that goes with the button's
   size. An icon-only button grows to keep the icon at half its width. */
.tiq-sb--sized-icon .tiq-sb__icon { width: var(--sb-icon-size); height: var(--sb-icon-size); }
.tiq-sb--icon.tiq-sb--sized-icon { width: max(var(--sb-min, 52px), calc(var(--sb-icon-size) * 2)); height: max(var(--sb-min, 52px), calc(var(--sb-icon-size) * 2)); }
.tiq-sb--icon.tiq-sb--s { --sb-min: 40px; }
.tiq-sb--icon.tiq-sb--l { --sb-min: 68px; }

/* Alignment inside a Button (a row): horizontal moves the icon and
   words along it and lines up the words; vertical sets where the icon
   sits against two lines of words. */
.tiq-sb--button.tiq-sb--h-center { justify-content: center; }
.tiq-sb--button.tiq-sb--h-right { justify-content: flex-end; }
.tiq-sb--button.tiq-sb--v-top { align-items: flex-start; }
.tiq-sb--button.tiq-sb--v-bottom { align-items: flex-end; }

/* Inside a Box (a column): horizontal lines the icon and words up
   across it; vertical places them when the box is taller than they
   are, as it is beside a longer neighbor. */
.tiq-sb--card.tiq-sb--h-center { align-items: center; }
.tiq-sb--card.tiq-sb--h-right { align-items: flex-end; }
.tiq-sb--card.tiq-sb--v-center { justify-content: center; }
.tiq-sb--card.tiq-sb--v-bottom { justify-content: flex-end; }

.tiq-sb--h-center .tiq-sb__words { text-align: center; align-items: center; }
.tiq-sb--h-right .tiq-sb__words { text-align: right; align-items: flex-end; }
</style>
